import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MapPin, Play, ArrowRight } from "lucide-react";
import { PROJECTS } from "../data/site";
import { scrollToId } from "../utils/scroll";

const Tile = ({ project, index, onOpen }) => (
  <motion.button
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{
      delay: (index % 3) * 0.08,
      duration: 1,
      ease: [0.16, 1, 0.3, 1],
    }}
    onClick={() => onOpen(project)}
    data-testid={`portfolio-tile-${project.id}`}
    className={`group relative block overflow-hidden bg-navy-900 text-left ${project.span}`}
  >
    <img
      src={project.url}
      alt={project.title}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
    />
    <div className="absolute inset-0 bg-navy-950/0 transition-colors duration-700 group-hover:bg-navy-950/55" />
    <div className="absolute inset-x-0 bottom-0 translate-y-5 p-6 opacity-0 transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0 group-hover:opacity-100 md:p-8">
      <p className="text-[0.55rem] font-semibold uppercase tracking-[0.35em] text-gold">
        {project.category}
      </p>
      <h3 className="mt-2 font-serif text-lg font-semibold text-white md:text-xl">
        {project.title}
      </h3>
    </div>
  </motion.button>
);

const VideoTile = () => (
  <motion.div
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ delay: 0.16, duration: 1, ease: [0.16, 1, 0.3, 1] }}
    data-testid="portfolio-video-tile"
    className="group relative col-span-12 flex flex-col items-center justify-center gap-5 border border-navy-800/15 bg-navy-900 p-10 sm:col-span-6 lg:col-span-4"
  >
    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/40 transition-colors duration-700 group-hover:bg-gold/10">
      <Play className="h-5 w-5 text-gold" strokeWidth={1.25} />
    </div>
    <div className="text-center">
      <p className="font-serif text-lg font-semibold text-white">
        Cinematic Walkthroughs
      </p>
      <p className="mt-2 text-xs font-light tracking-wide text-slate-400">
        Full 4K video tours shared during private consultations
      </p>
    </div>
  </motion.div>
);

const Lightbox = ({ project, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }}
    data-testid="portfolio-lightbox"
    className="fixed inset-0 z-[90] flex items-center justify-center bg-navy-950/92 p-4 backdrop-blur-md md:p-10"
    onClick={onClose}
  >
    <button
      onClick={onClose}
      aria-label="Close project view"
      data-testid="lightbox-close-button"
      className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors duration-500 hover:border-gold hover:text-gold"
    >
      <X className="h-5 w-5" strokeWidth={1.25} />
    </button>
    <motion.figure
      initial={{ scale: 0.94, y: 30 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.96, y: 20 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-5xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="overflow-hidden">
        <img
          src={project.url}
          alt={project.title}
          className="max-h-[68vh] w-full object-cover"
        />
      </div>
      <figcaption className="flex flex-col gap-6 border border-white/10 bg-navy-900/80 p-6 backdrop-blur-xl md:flex-row md:items-center md:justify-between md:p-8">
        <div>
          <p className="text-[0.55rem] font-semibold uppercase tracking-[0.35em] text-gold">
            {project.category}
          </p>
          <h3 className="mt-2 font-serif text-xl font-semibold text-white md:text-2xl">
            {project.title}
          </h3>
          <p className="mt-2 flex items-center gap-2 text-xs font-light tracking-wide text-slate-400">
            <MapPin className="h-3.5 w-3.5 text-gold/70" strokeWidth={1.5} />
            {project.location}
          </p>
        </div>
        <button
          onClick={() => {
            onClose();
            scrollToId("#contact");
          }}
          data-testid="lightbox-inquiry-button"
          className="group relative shrink-0 overflow-hidden border border-gold/60 px-7 py-3.5"
        >
          <span className="relative z-10 flex items-center gap-3 text-[0.6rem] font-semibold uppercase tracking-[0.28em] text-white transition-colors duration-500 group-hover:text-navy-900">
            Enquire About This Project
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </span>
          <span className="absolute inset-0 origin-left scale-x-0 bg-gold transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-x-100" />
        </button>
      </figcaption>
    </motion.figure>
  </motion.div>
);

export default function Portfolio() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-[#F8FAFC] px-6 py-28 md:px-10 md:py-40"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-16 flex flex-col gap-8 md:mb-24 md:flex-row md:items-end md:justify-between">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="text-[0.6rem] font-semibold uppercase tracking-[0.45em] text-gold md:text-xs"
            >
              Selected Works
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-5 font-serif text-2xl font-semibold leading-tight text-navy-800 sm:text-3xl lg:text-4xl"
              data-testid="portfolio-heading"
            >
              A Portfolio of
              <br />
              Quiet Luxury.
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 1 }}
            className="max-w-sm text-sm font-light leading-relaxed text-slate-500"
          >
            An editorial selection of residential and commercial transformations —
            each space composed, crafted and finished entirely in-house.
          </motion.p>
        </div>

        <div className="grid grid-cols-12 gap-3 [grid-auto-rows:230px] [grid-auto-flow:dense] md:gap-4 md:[grid-auto-rows:270px]">
          {PROJECTS.map((project, i) => (
            <Tile key={project.id} project={project} index={i} onOpen={setActive} />
          ))}
          <VideoTile />
        </div>
      </div>

      <AnimatePresence>
        {active && <Lightbox project={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
